import axios from 'axios';
import config from 'config';

import { notifyFailure } from './Notifications';
import { hideProgressBar, showProgressBar } from './ProgressBar';
import { isFalsy } from '../lib/Helpers';
import auth0 from '../lib/Auth0';

const defaultState = {
  error: false,
  pendingTasks: 0,
};

export const axiosClient = axios.create({ baseURL: config.api.url });

/**
 * Adds access_token from localStorage to headers for all API requests
 * @return {Object} data w/ bearer token included.
 */
export const headers = (isFileUpload) => {
  const data = {};
  /* eslint-disable no-undef */
  if (isFileUpload) {
    data['Content-Type'] = 'multipart/form-data';
  }
  return data;
};

export const REQUEST_DATA = 'REQUEST_DATA';
/**
 * Request Data Handler to emit action & update pendingTasks count
 * @return {Object} Request_Data Payload
 */
export const requestData = () => {
  return (dispatch, getState) => {
    const newTaskCount = getState().axios.pendingTasks + 1;
    if (newTaskCount === 1) dispatch(showProgressBar());

    return dispatch({
      type: REQUEST_DATA,
      payload: newTaskCount,
    });
  };
};

export const RESPONSE_DATA = 'RESPONSE_DATA';
/**
 * Response Data Handler to emit action & update pendingTasks count
 * @return {Object} Reponse_Data Payload
 */
export const responseData = () => {
  return (dispatch, getState) => {
    const newTaskCount = getState().axios.pendingTasks - 1;
    if (isFalsy(newTaskCount)) dispatch(hideProgressBar());

    return dispatch({
      type: RESPONSE_DATA,
      payload: newTaskCount,
    });
  };
};

export const RESPONSE_ERROR = 'RESPONSE_ERROR';
/**
 * Response Error handler to emit Notification & redirect to `/login`
 * @param {Object} e - Error Object
 * @return {Object} Response_Error Payload
 */
export const responseError = e => {
  return (dispatch, getState) => {
    const newTaskCount = getState().axios.pendingTasks - 1;
    if (isFalsy(newTaskCount)) dispatch(hideProgressBar());

    dispatch({ type: RESPONSE_ERROR, payload: newTaskCount });
    /* Use the history push store in state to redirect to `/login` */

    if (e.response && e.response.status === 401) {
      notifyFailure('Redirecting to Login');
      return window.location = '/login';
    }
    return e;
  };
};

/**
 * Request Handler -- Single Request handler for HTTP requests (GET, POST, PUT, DELETE)
 * @param {Object} opts - Request Parameters (Header, Body, etc)
 * @return {Object} response.data || error
 */
export const request = opts => {
  return async dispatch => {
    dispatch(requestData());
    const token = await auth0.getAccessTokenSilently();
    if (token) {
      opts.headers.Authorization = `Bearer ${token}`;
    }
    const response = await axiosClient(opts)
      .catch(e => {
        dispatch(responseError(e));
        throw e;
      });
    dispatch(responseData());
    /* In case we need some additional information from the response than just response.data */
    return opts.returnAll
      ? response
      : response.data;
  };
};

/**
 * GET Request Handler -- Passes to Request()
 * @param {string} url - API Endpoint Path
 * @param {any} params - Request Parameters
 * @return {Function} request()
 */
export const fetchData = (url, params = {}, customHeaders = {}, returnAll = false, responseType = 'json') => {
  return request({
    method: 'GET',
    url,
    /* Concats with the token headers if we require custom-headers to be passed for sensitive data over HTTPS */
    headers: { ...headers(), ...customHeaders },
    timeout: 120000,
    /* TODO: Investigate Exactly What This is Doing? */
    responseType,
    ...params,
    /* returnAll tells request to return the entire response payload, not just response.data */
    returnAll,
  });
};

/**
 * POST Request Handler -- Passes to Request()
 * @param {string} url - API Endpoint Path
 * @param {any} data - Request Body
 * @param {any} params - Request Parameters
 * @return {Function} request()
 */
export const postData = (url, data, params, isFileUpload, responseType = 'json', fileUplodingCallback) => {
  return request({
    method: 'POST',
    url,
    headers: headers(isFileUpload),
    onUploadProgress: progressEvent => {
      if (isFileUpload && fileUplodingCallback) { fileUplodingCallback(Math.round((progressEvent.loaded * 100) / progressEvent.total)); }
    },
    timeout: isFileUpload ? 86400000 : 120000, // 1 day for file uploads (MKC-5039)
    responseType,
    data,
    ...params,
  });
};

/**
 * PUT Request Handler -- Passes to Request()
 * @param {string} url - API Endpoint Path
 * @param {any} data - Request Body
 * @param {any} params - Request Parameters
 * @return {Function} request()
 */
export const putData = (url, data, params) => {
  return request({
    method: 'PUT',
    url,
    headers: headers(),
    data,
    ...params,
  });
};

/**
 * PATCH Request Handler -- Passes to Request()
 * @param {string} url - API Endpoint Path
 * @param {any} data - Request Body
 * @param {any} params - Request Parameters
 * @return {Function} request()
 */
export const patchData = (url, data, params) => {
  return request({
    method: 'PATCH',
    url,
    headers: headers(),
    data,
    ...params,
  });
};

/**
 * DELETE Request Handler -- Passes to Request()
 * @param {string} url - API Endpoint Path
 * @param {any} body - Request Body
 * @param {any} params - Request Parameters
 * @return {Function} request()
 */
export const deleteData = (url, data, params) => {
  return request({
    method: 'DELETE',
    url,
    headers: headers(),
    data,
    ...params,
  });
};

/* Export API Handlers */
export const actions = {
  fetchData,
  postData,
  putData,
  deleteData,
};

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case REQUEST_DATA:
      return { ...state, pendingTasks: action.payload };
    case RESPONSE_DATA:
      return { ...state, pendingTasks: action.payload, error: false };
    case RESPONSE_ERROR:
      return { ...state, pendingTasks: action.payload, error: true };
    default:
      return state;
  }
}
